import { useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useConvexAuth, useQuery } from 'convex/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, ShieldCheck } from 'lucide-react';
import { useOrgContext } from '@/hooks/useOrgContext';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { PreAuthApplicationWizard } from '@/components/insurance/preauth-application/PreAuthApplicationWizard';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/customer/ require three levels up to reach convex/_generated/
import type { IFormStep } from '@/@types/policyFormTemplates';
import type { IPreAuthApplicationData, IPortalPolicy } from '@/@types/claims';
import { cn } from '@/lib/utils';

function formatDate(ts: number | undefined): string {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString();
}

export function NewPreAuthPage() {
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.startsWith('/company') ? '/company/my-insurance' : '/my-insurance';
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const { currentCompany, isLoading: companyLoading } = useCompanyContext();
  const currentMembership = useQuery(api.companies.members.getCurrentMembership);
  const [searchParams] = useSearchParams();
  const draftIdParam = searchParams.get('draftId');

  const [selectedPolicyId, setSelectedPolicyId] = useState<string>('');
  const saveDraftRef = useRef<(() => Promise<void>) | null>(null);

  // Load selected policy to get insuranceTypeId and orgId for template query
  const selectedPolicyData = useQuery(
    api['insurance/policies'].get,
    selectedPolicyId
      ? { policyId: selectedPolicyId as Id<'insurancePolicies'> } // AS_JUSTIFIED: selectedPolicyId from listForPortalUser policy _id, safe to narrow to typed Id
      : 'skip'
  );

  // Resolve orgId: org members → currentOrg, company members → company's orgId,
  // simple customers → orgId from selected policy
  const effectiveOrgId =
    currentOrg?._id ?? currentCompany?.orgId ?? selectedPolicyData?.orgId ?? null;

  // Resolve claimant type and ID dynamically based on route context
  const isCompanyRoute = pathname.startsWith('/company');
  const resolvedClaimantType: 'employee' | 'customer' = isCompanyRoute ? 'employee' : 'customer';
  const resolvedClaimantId: string = isCompanyRoute
    ? String(currentMembership?.employeeRecordId ?? '')
    : String(selectedPolicyData?.leadId ?? '');

  // Query for provider-specific pre-auth form template with multi-step wizard
  const preAuthTemplates = useQuery(
    api['insurance/claimFormTemplates'].list,
    effectiveOrgId && selectedPolicyData?.insuranceTypeId
      ? { orgId: effectiveOrgId, insuranceTypeId: selectedPolicyData.insuranceTypeId }
      : 'skip'
  );
  const wizardTemplate = preAuthTemplates?.find(
    (t: { steps?: unknown; templateCategory?: string; name?: string }) =>
      Array.isArray(t.steps) &&
      t.steps.length > 0 &&
      (t.templateCategory === 'pre_auth' || t.name?.toLowerCase().includes('pre-auth'))
  );

  // Load draft pre-auth for resume
  const draftPreAuthData = useQuery(
    api.insurance.preAuthorizations.get,
    draftIdParam ? { preAuthId: draftIdParam as Id<'preAuthorizations'> } : 'skip' // AS_JUSTIFIED: draftId URL param from MyPreAuthsListPage navigation with valid preAuth ID
  );

  const allPolicies = useQuery(api.insurance.policies.listForPortalUser);

  if (authLoading || orgLoading || companyLoading) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="p-8 text-muted-foreground">
        Please sign in to submit a pre-authorization request.
      </div>
    );
  }

  const needsOrgId = selectedPolicyId !== '' && !effectiveOrgId;
  if (needsOrgId) {
    return (
      <div className="p-8 text-muted-foreground">
        No insurance organisation found. Please contact your employer or insurance provider.
      </div>
    );
  }

  // Auto-advance to wizard when resuming a draft
  const isDraftResume = draftIdParam && draftPreAuthData?.preAuthApplicationData;
  const draftPolicyId = draftPreAuthData?.policyId;

  if (isDraftResume && draftPolicyId && selectedPolicyId !== String(draftPolicyId)) {
    setSelectedPolicyId(String(draftPolicyId));
  }

  const activePolicyId = draftPolicyId ? String(draftPolicyId) : selectedPolicyId;
  const inWizardMode = activePolicyId !== '' && wizardTemplate;

  if (inWizardMode) {
    const typedTemplateId = wizardTemplate._id as Id<'claimFormTemplates'>; // AS_JUSTIFIED: template _id from Convex query result, safe to narrow to typed Id
    const typedPolicyId = activePolicyId as Id<'insurancePolicies'>; // AS_JUSTIFIED: policyId from draft or policy selection, originates from Convex query
    const typedExistingId = draftIdParam ? (draftIdParam as Id<'preAuthorizations'>) : undefined; // AS_JUSTIFIED: draftId URL param from MyPreAuthsListPage
    const typedExistingData = isDraftResume
      ? (draftPreAuthData.preAuthApplicationData as IPreAuthApplicationData) // AS_JUSTIFIED: preAuthApplicationData shape matches IPreAuthApplicationData from seed template
      : undefined;

    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-6 py-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedPolicyId('')}
              className="gap-2 text-zinc-900"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-base font-bold text-foreground">New Pre-Authorization</h1>
              <p className="text-xs text-muted-foreground">{wizardTemplate.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`${basePath}/pre-authorizations`)}
            >
              Cancel
            </Button>
            <Button variant="outline" size="sm" onClick={() => void saveDraftRef.current?.()}>
              Save Draft
            </Button>
          </div>
        </div>
        <PreAuthApplicationWizard
          templateId={typedTemplateId}
          templateSteps={wizardTemplate.steps as IFormStep[]} // AS_JUSTIFIED: steps validated as non-empty array above; IFormStep[] matches seed template structure
          templateName={wizardTemplate.name}
          orgId={effectiveOrgId!} // AS_JUSTIFIED: effectiveOrgId is guaranteed non-null when in wizard mode (needsOrgId guard above)
          policyId={typedPolicyId}
          existingPreAuthId={typedExistingId}
          existingData={typedExistingData}
          claimantType={resolvedClaimantType}
          claimantId={resolvedClaimantId}
          onBack={() => setSelectedPolicyId('')}
          onComplete={() => navigate(`${basePath}/pre-authorizations`)}
          onRegisterSaveDraft={fn => {
            saveDraftRef.current = fn;
          }}
        />
      </div>
    );
  }

  // If a policy is selected but templates are still loading
  if (selectedPolicyId && preAuthTemplates === undefined) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // If a policy is selected but no matching pre-auth template found
  if (selectedPolicyId && preAuthTemplates !== undefined && !wizardTemplate) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center gap-4 border-b border-zinc-200 bg-white px-8 py-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedPolicyId('')}
            className="gap-2 text-zinc-900"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </Button>
          <h1 className="text-lg font-bold text-zinc-900">New Pre-Authorization</h1>
        </div>
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center text-muted-foreground max-w-sm">
            <ShieldCheck className="mx-auto mb-3 h-10 w-10 opacity-40" />
            <p className="font-medium">
              No pre-authorization form template configured for this policy
            </p>
            <p className="text-sm mt-1">
              Please contact your insurance provider to set up this form.
            </p>
            <Button variant="outline" className="mt-4" onClick={() => setSelectedPolicyId('')}>
              Choose a different policy
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Step 0: Policy selection
  const policies = (allPolicies as IPortalPolicy[] | undefined)?.filter(p => p.status === 'active'); // AS_JUSTIFIED: allPolicies from listForPortalUser matches IPortalPolicy shape; anyApi loses the return type

  if (policies === undefined) {
    return (
      <div className="p-8 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between border-b border-zinc-200 bg-white px-8 py-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="gap-2 text-zinc-900"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </Button>
          <div>
            <h1 className="text-lg font-bold text-zinc-900">New Pre-Authorization</h1>
            <p className="text-[13px] text-zinc-500">Select a policy to begin</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-zinc-50">
        <div className="px-8 py-6 space-y-5">
          {policies.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <ShieldCheck className="mx-auto mb-3 h-10 w-10 opacity-40" />
              <p className="font-medium">No active policies found</p>
              <p className="text-sm">
                You need an active insurance policy to submit a pre-authorization request.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select the policy this pre-authorization request is for.
              </p>
              <div className="space-y-3">
                {policies.map(policy => (
                  <button
                    key={policy._id}
                    type="button"
                    onClick={() => setSelectedPolicyId(policy._id)}
                    className={cn(
                      'w-full rounded-lg border p-4 text-left transition-colors hover:bg-muted/50',
                      selectedPolicyId === policy._id
                        ? 'border-primary bg-primary/5 ring-1 ring-primary'
                        : 'border-border'
                    )}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-1">
                        <p className="font-medium">{policy.policyNumber}</p>
                        {policy.insuranceTypeName && (
                          <p className="text-sm text-muted-foreground">
                            {policy.insuranceTypeName}
                          </p>
                        )}
                        {policy.expiresAt && (
                          <p className="text-xs text-muted-foreground">
                            Expires: {formatDate(policy.expiresAt)}
                          </p>
                        )}
                      </div>
                      <Badge variant="secondary">{policy.status}</Badge>
                    </div>
                  </button>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  disabled={!selectedPolicyId}
                  onClick={() => setSelectedPolicyId(selectedPolicyId)}
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
